import React, { useContext } from "react";
import { TrendingUp } from "lucide-react";
import { ThemeContext } from "../constant/theme";

// Inline helper to avoid importing from outside `src/`.
const hexToRgba = (hex, alpha = 0.12) => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getStatIconBackground = (color) => {
  if (!color) return 'rgba(0,0,0,0.06)';
  if (typeof color === 'string' && color.startsWith('#')) return hexToRgba(color, 0.12);
  return color;
};

const StatsGrid = ({ 
  styles = {}, 
  stats = [], 
  screenSize = {}, 
  defaultSymbolBgAlpha = 0.12,
  gridStyle = {}, // Allow custom grid wrapper style
  layout: gridLayout, // Default layout for every card ('inline' | 'horizontal'); per-stat `layout` wins
}) => {
  const { theme } = useContext(ThemeContext);
  const isMobile = screenSize.isMobile || screenSize === "xs" || screenSize === "sm";
  const isTablet = screenSize === "md";

  const renderContent = (content) => {
    if (React.isValidElement(content)) return content;
    if (typeof content === 'function') return React.createElement(content, {});
    // Preserve numeric 0 and boolean false; treat only null/undefined as empty
    if (content === null || content === undefined) return '';
    return String(content);
  };

  // Use parent's gridTemplateColumns if provided, otherwise use responsive defaults
  const gridTemplateColumns = gridStyle?.gridTemplateColumns || (
    isMobile
      ? "1fr"
      : isTablet
        ? "repeat(2, 1fr)"
        : "repeat(4, 1fr)"
  );

  const isRTL = styles.container?.direction === "rtl" || document.dir === "rtl";

  return (
    <div className="nx-kpi-grid" style={{ 
      ...(styles.statsGrid || {}), 
      ...gridStyle,
      display: "grid",
      gridTemplateColumns,
      gap: isMobile ? "12px" : isTablet ? "16px" : "20px",
      transition: "all 0.30s ease",
    }}>
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        const layout = stat.layout || gridLayout;

        // Per-card options with sensible defaults
        const showSymbol = stat.showSymbol !== undefined ? stat.showSymbol : true;
        const showSubtitle = stat.showSubtitle !== undefined ? stat.showSubtitle : !!stat.subtitle;
        const cardHeightStyle = stat.cardHeight ? { height: stat.cardHeight } : {};
        const cardWidthStyle = stat.cardWidth
          ? { width: typeof stat.cardWidth === 'number' ? `${stat.cardWidth}px` : stat.cardWidth, justifySelf: 'center' }
          : {};
        const rawValueAlign = stat.valueAlign ?? styles?.statValue?.textAlign ?? 'left';
        let valueAlignStyle = {};
        if (typeof rawValueAlign === 'number') {
          // use logical margin so it respects RTL/LTR (`marginInlineStart`)
          valueAlignStyle = { marginInlineStart: `${rawValueAlign}px` };
        } else {
          valueAlignStyle = { textAlign: rawValueAlign };
        }
        const valueCustomStyle = stat.valueStyle || {};

        const iconBg = (stat.iconBackground === null)
          ? 'transparent'
          : getStatIconBackground(stat.iconBackground || stat.color || undefined, defaultSymbolBgAlpha);

        const headerGap = stat.titleGap !== undefined ? stat.titleGap : styles?.statHeader?.gap;
        const baseHeaderStyle = headerGap !== undefined ? { ...styles.statHeader, gap: headerGap } : styles.statHeader;
        const headerStyle = layout === 'horizontal' ? { ...baseHeaderStyle, display: 'flex', flexDirection: 'row', alignItems: 'center' } : baseHeaderStyle;
        const titleStyle = { ...(styles.statTitle || {}), ...(stat.titleStyle || {}) };
        const subtitleStyle = { ...(styles.statSubtitle || {}), ...(stat.subtitleStyle || {}) };

        // Allow full card style override per stat
        const cardStyle = stat.cardStyle ? { ...(styles.statCard || {}), ...stat.cardStyle } : styles.statCard;

        return (
          <div
            key={index}
            style={{ 
              ...cardStyle, 
              ...cardHeightStyle, 
              ...cardWidthStyle,
              background: theme === "dark"
                ? "rgba(255,255,255,0.01)"
                : "rgba(255,255,255,1)",
              padding: isMobile ? "16px" : isTablet ? "18px" : "20px",
              borderRadius: isMobile ? "12px" : "16px",
              transition: "all 0.3s ease",
              boxShadow: theme === "dark"
                ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
                : "0 8px 32px rgba(0,0,0,0.1)",
              backdropFilter: theme === "dark" ? "blur(10px)" : "blur(0px)",
              WebkitBackdropFilter: theme === "dark" ? "blur(10px)" : "blur(0px)",
              border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.05)",
              cursor: stat.onClick ? "pointer" : "default",
              position: "relative",
              overflow: "hidden",
            }}
            onClick={stat.onClick}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = theme === "dark"
                ? "0 12px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)"
                : "0 12px 40px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = theme === "dark"
                ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
                : "0 8px 32px rgba(0,0,0,0.1)";
            }}
          >
            {layout === 'inline' ? (
              // Inline: [icon] [title ........ value] on one row — the number
              // closes the line instead of dropping under the label. Flex
              // order follows the document direction, so RTL mirrors natively.
              <div className="nx-kpi-inline" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px', minWidth: 0, flexWrap: 'wrap' }}>
                {showSymbol && (
                  <div
                    style={{
                      ...styles.statIconWrapper,
                      background: stat.iconBackground !== undefined ? stat.iconBackground : iconBg,
                      width: isMobile ? "36px" : "40px",
                      height: isMobile ? "36px" : "40px",
                      borderRadius: isMobile ? "6px" : "8px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#fff",
                      flexShrink: 0,
                      ...(stat.iconWrapperStyle || {}),
                    }}
                  >
                    {Icon ? <Icon size={isMobile ? 18 : 22} /> : null}
                  </div>
                )}
                <p className="nx-kpi-label" style={{
                  ...titleStyle,
                  flex: 1,
                  minWidth: "60px",
                  margin: 0,
                  fontSize: isMobile ? "13px" : "14px",
                  color: theme === "dark"
                    ? "rgba(255,255,255,0.75)"
                    : "rgba(26,26,26,0.65)",
                  fontWeight: "500",
                  lineHeight: 1.3,
                  textAlign: "start",
                  // Opt-in `wrapTitle`: show the full title on as many lines
                  // as needed instead of cutting it with an ellipsis.
                  ...(stat.wrapTitle
                    ? { whiteSpace: "normal", overflowWrap: "break-word" }
                    : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
                }}>{renderContent(stat.title)}</p>
                {stat.value != null && (
                  <h2 style={{
                    ...styles.statValue,
                    ...valueCustomStyle,
                    flexShrink: 0,
                    margin: 0,
                    fontSize: isMobile ? "18px" : "20px",
                    fontWeight: "700",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                    color: theme === "dark" ? "#fff" : "#111",
                    textAlign: "end",
                    marginInlineStart: "auto",
                  }}>
                    {renderContent(stat.value)}
                  </h2>
                )}
              </div>
            ) : (
            <>
            <div style={headerStyle}>
              {showSymbol && (
                <div
                  style={{
                    ...styles.statIconWrapper,
                    background: stat.iconBackground !== undefined ? stat.iconBackground : iconBg,
                    width: isMobile ? "36px" : "40px",
                    height: isMobile ? "36px" : "40px",
                    borderRadius: isMobile ? "6px" : "8px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#fff",
                    flexShrink: 0,
                    marginRight: isMobile ? "0" : (isRTL ? "0" : "12px"),
                    marginLeft: isMobile ? "0" : (isRTL ? "12px" : "0"),

                    ...(stat.iconWrapperStyle || {}),
                  }}
                >
                  {Icon ? <Icon size={isMobile ? 18 : 24} /> : null}
                </div>
              )}
              {layout === 'horizontal' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <p style={{
                    ...titleStyle,
                    fontSize: isMobile ? "12px" : "14px",
                    color: theme === "dark"
                      ? "rgba(255,255,255,0.9)"
                      : "rgba(26,26,26,0.7)",
                    fontWeight: "500",
                    lineHeight: 1.3,
                    margin: 0,
                  }}>{renderContent(stat.title)}</p>
                  {stat.value != null && (
                    <h2 style={{ 
                      ...styles.statValue, 
                      ...valueAlignStyle, 
                      ...valueCustomStyle,
                      fontSize: isMobile ? "20px" : isTablet ? "22px" : "24px",
                      fontWeight: "700",
                      color: theme === "dark" ? "#fff" : "#000",
                      textAlign: isMobile ? "center" : (isRTL ? "right" : "left"),
                      margin: 0,
                    }}>
                      {renderContent(stat.value)}
                    </h2>
                  )}
                </div>
              ) : (
                <p style={{
                  ...titleStyle,
                  fontSize: isMobile ? "12px" : "14px",
                  color: theme === "dark"
                    ? "rgba(255,255,255,0.9)"
                    : "rgba(26,26,26,0.7)",
                  fontWeight: "500",
                  lineHeight: 1.3,
                  margin: 0,
                }}>{renderContent(stat.title)}</p>
              )}
            </div>
            {layout !== 'horizontal' && (
              <h2 style={{ 
                ...styles.statValue, 
                ...valueAlignStyle, 
                ...valueCustomStyle,
                fontSize: isMobile ? "20px" : isTablet ? "22px" : "24px",
                fontWeight: "700",
                color: theme === "dark" ? "#fff" : "#000",
                textAlign: isMobile ? "center" : (isRTL ? "right" : "left"),
                margin: 0,
                marginBottom: isMobile ? "4px" : "0",
                marginRight: 8,
              }}>
                {renderContent(stat.value)}
              </h2>
            )}
            </>
            )}
            {showSubtitle && stat.subtitle ? (
              <p style={{
                ...subtitleStyle,
                fontSize: isMobile ? "12px" : "14px",
                color: theme === "dark"
                  ? "rgba(255,255,255,0.6)"
                  : "rgba(26,26,26,0.6)",
                margin: "8px 0 0 0",
                textAlign: isMobile ? "center" : (isRTL ? "right" : "left"),
              }}>
                {renderContent(stat.subtitle)}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default StatsGrid;